<template>
  <div class="activity-logs">
    <!-- Admin Access Check -->
    <div v-if="!isUserAdmin" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <font-awesome-icon icon="shield-alt" class="text-4xl text-red-500 mb-4" />
      <h2 class="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
      <p class="text-red-600">This feature is only available for administrators.</p>
      <el-button @click="$router.push('/dashboard')" class="mt-4">
        <font-awesome-icon icon="arrow-left" class="mr-1" />
        Back to Dashboard
      </el-button>
    </div>

    <!-- Admin Content -->
    <div v-else class="bg-white rounded-lg shadow-md p-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">
          <font-awesome-icon icon="history" class="mr-2" />
          Activity Logs
        </h1>
        <div class="flex gap-3">
          <el-button
            type="info"
            size="small"
            @click="showFilters = !showFilters"
          >
            <font-awesome-icon :icon="showFilters ? 'eye-slash' : 'filter'" class="mr-1" />
            {{ showFilters ? 'Hide' : 'Show' }} Filters
          </el-button>
          <el-button
            type="success"
            size="small"
            @click="exportLogs('excel')"
            :disabled="loading"
          >
            <font-awesome-icon icon="file-excel" class="mr-1" />
            Export Excel
          </el-button>
          <el-button
            type="primary"
            size="small"
            @click="refreshLogs"
            :loading="loading"
          >
            <font-awesome-icon icon="sync-alt" class="mr-1" />
            Refresh
          </el-button>
        </div>
      </div>

      <!-- Filters Section -->
      <div v-if="showFilters" class="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 class="text-lg font-semibold text-gray-700 mb-4">Filter Options</h3>
        <el-form :model="filters" inline class="filter-form">
          <el-form-item label="Username">
            <el-input
              v-model="filters.username"
              placeholder="Enter username"
              clearable
              class="w-48"
            />
          </el-form-item>
          
          <el-form-item label="Action">
            <el-select v-model="filters.action" placeholder="Select action" clearable class="w-48">
              <el-option label="Absen Pagi" value="ABSEN_PAGI" />
              <el-option label="Absen Siang" value="ABSEN_SIANG" />
              <el-option label="Absen Sore" value="ABSEN_SORE" />
            </el-select>
          </el-form-item>

          <el-form-item label="Device Owner">
            <el-input
              v-model="filters.deviceOwner"
              placeholder="Enter device owner name"
              clearable
              class="w-48"
            />
          </el-form-item>

          <el-form-item label="Anomali">
            <el-select v-model="filters.anomali" placeholder="Filter anomali" clearable class="w-48">
              <el-option label="Semua" value="" />
              <el-option label="Normal" value="false" />
              <el-option label="Anomali" value="true" />
            </el-select>
          </el-form-item>

          <el-form-item label="Date Range">
            <el-date-picker
              v-model="dateRange"
              type="datetimerange"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="dd/MM/yyyy HH:mm"
              value-format="yyyy-MM-dd HH:mm:ss"
              class="w-80"
              @change="onDateRangeChange"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="applyFilters">
              <font-awesome-icon icon="search" class="mr-1" />
              Apply Filters
            </el-button>
            <el-button @click="clearFilters">
              <font-awesome-icon icon="times" class="mr-1" />
              Clear
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <el-card class="text-center">
          <div class="text-2xl font-bold text-blue-600 mb-2">{{ totalLogs }}</div>
          <div class="text-gray-600">Total Logs</div>
        </el-card>
        <el-card class="text-center">
          <div class="text-2xl font-bold text-green-600 mb-2">{{ todayLogs }}</div>
          <div class="text-gray-600">Today's Logs</div>
        </el-card>
        <el-card class="text-center">
          <div class="text-2xl font-bold text-orange-600 mb-2">{{ uniqueUsers }}</div>
          <div class="text-gray-600">Active Users</div>
        </el-card>
        <el-card class="text-center">
          <div class="text-2xl font-bold text-purple-600 mb-2">{{ loginCount }}</div>
          <div class="text-gray-600">Activities Today</div>
        </el-card>
      </div>

      <!-- Activity Logs Table -->
      <div v-if="activityLogs.length > 0">
        <el-table
          :data="activityLogs"
          stripe
          style="width: 100%"
          v-loading="loading"
          :default-sort="{prop: 'timestamp', order: 'descending'}"
        >
          <el-table-column
            prop="timestamp"
            label="Tanggal"
            min-width="160"
            sortable
          >
            <template slot-scope="scope">
              <div class="flex items-center">
                <font-awesome-icon icon="clock" class="mr-2 text-gray-400" />
                {{ formatDate(scope.row.timestamp) }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="username"
            label="Username"
            min-width="150"
          >
            <template slot-scope="scope">
              <div class="flex items-center">
                <font-awesome-icon icon="user" class="mr-2 text-blue-500" />
                {{ scope.row.username }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="action"
            label="Action"
            width="130"
          >
            <template slot-scope="scope">
              <el-tag 
                :type="getActionTagType(scope.row.action)" 
                size="mini"
              >
                {{ formatAction(scope.row.action) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
            label="Anomali"
            width="100"
          >
            <template slot-scope="scope">
              <el-tag 
                :type="isAnomalous(scope.row) ? 'danger' : 'success'" 
                size="mini"
              >
                {{ isAnomalous(scope.row) ? 'Anomali' : 'Normal' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
            prop="deviceOwnerName"
            label="Device Owner"
            min-width="150"
          >
            <template slot-scope="scope">
              <div class="flex items-center text-sm" v-if="scope.row.deviceOwnerName">
                <font-awesome-icon icon="user-tag" class="mr-2 text-green-500" />
                {{ scope.row.deviceOwnerName }}
              </div>
              <div class="text-sm text-gray-400" v-else>
                <font-awesome-icon icon="user-slash" class="mr-2" />
                Unknown
              </div>
            </template>
          </el-table-column>

          <el-table-column
            label="Actions"
            width="100"
            fixed="right"
          >
            <template slot-scope="scope">
              <el-button
                type="text"
                size="mini"
                @click="showLogDetail(scope.row)"
              >
                <font-awesome-icon icon="eye" class="mr-1" />
                Detail
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- Pagination -->
        <div class="flex justify-center mt-6">
          <el-pagination
            @current-change="handlePageChange"
            :current-page="currentPage + 1"
            :page-size="pageSize"
            layout="prev, pager, next, jumper, ->, sizes, total"
            :total="totalElements"
            :page-sizes="[10, 25, 50, 100]"
            @size-change="handleSizeChange"
          />
        </div>
      </div>

      <!-- No logs message -->
      <div v-else-if="!loading" class="text-center py-8">
        <font-awesome-icon icon="history" class="text-6xl text-gray-300 mb-4" />
        <p class="text-gray-500 text-lg">No activity logs found</p>
        <p class="text-gray-400 text-sm">Try adjusting your filters or check back later</p>
      </div>
    </div>

    <!-- Log Detail Dialog -->
    <el-dialog
      title="Activity Log Detail"
      :visible.sync="showDetailDialog"
      width="700px"
    >
      <div v-if="selectedLog" class="space-y-4">
        <!-- Basic Information -->
        <div class="bg-gray-50 p-4 rounded-lg">
          <h4 class="text-sm font-semibold text-gray-800 mb-3">Basic Information</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Timestamp</label>
              <p class="text-sm text-gray-900">{{ formatDate(selectedLog.timestamp) }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Username</label>
              <p class="text-sm text-gray-900">{{ selectedLog.username }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Action</label>
              <el-tag :type="getActionTagType(selectedLog.action)" size="mini">
                {{ formatAction(selectedLog.action) }}
              </el-tag>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <el-tag :type="selectedLog.status === 'SUCCESS' ? 'success' : 'danger'" size="mini">
                {{ selectedLog.status || 'SUCCESS' }}
              </el-tag>
            </div>
          </div>
        </div>
        
        <!-- Network Information -->
        <div class="bg-green-50 p-4 rounded-lg">
          <h4 class="text-sm font-semibold text-green-800 mb-3">Network Information</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-green-700 mb-1">IP Address</label>
              <p class="text-sm text-green-900 font-mono">{{ selectedLog.ipAddress || 'Unknown' }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-green-700 mb-1">Session ID</label>
              <p class="text-sm text-green-900 font-mono">{{ selectedLog.sessionId || 'N/A' }}</p>
            </div>
          </div>
        </div>
        
        <!-- Device Information -->
        <div v-if="selectedLog.idDevice" class="bg-blue-50 p-4 rounded-lg">
          <h4 class="text-sm font-semibold text-blue-800 mb-3">Device Information</h4>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-xs font-medium text-blue-700 mb-1">Device ID</label>
              <p class="text-sm text-blue-900 font-mono">{{ selectedLog.idDevice }}</p>
            </div>
            <div v-if="selectedLog.deviceName">
              <label class="block text-xs font-medium text-blue-700 mb-1">Device Name</label>
              <p class="text-sm text-blue-900">{{ selectedLog.deviceName }}</p>
            </div>
            <div v-if="selectedLog.deviceType">
              <label class="block text-xs font-medium text-blue-700 mb-1">Device Type</label>
              <p class="text-sm text-blue-900">{{ selectedLog.deviceType }}</p>
            </div>
            <div v-if="selectedLog.deviceOwnerName">
              <label class="block text-xs font-medium text-blue-700 mb-1">Device Owner</label>
              <p class="text-sm text-blue-900">{{ selectedLog.deviceOwnerName }}</p>
            </div>
          </div>
        </div>
        
        <!-- Browser/User Agent Information -->
        <div class="bg-purple-50 p-4 rounded-lg">
          <h4 class="text-sm font-semibold text-purple-800 mb-3">Browser/Device Information</h4>
          <div>
            <label class="block text-xs font-medium text-purple-700 mb-1">Detected Device</label>
            <p class="text-sm text-purple-900 mb-2">{{ getDeviceInfo(selectedLog.userAgent) }}</p>
            
            <label class="block text-xs font-medium text-purple-700 mb-1">User Agent</label>
            <p class="text-xs text-purple-900 p-2 bg-purple-100 rounded break-all font-mono">
              {{ selectedLog.userAgent || 'Unknown' }}
            </p>
          </div>
        </div>
        
        <!-- Description -->
        <div v-if="selectedLog.description" class="bg-yellow-50 p-4 rounded-lg">
          <h4 class="text-sm font-semibold text-yellow-800 mb-3">Description</h4>
          <p class="text-sm text-yellow-900">
            {{ selectedLog.description || 'No description available' }}
          </p>
        </div>

        <!-- Additional Data -->
        <div v-if="selectedLog.additionalData" class="bg-gray-50 p-4 rounded-lg">
          <h4 class="text-sm font-semibold text-gray-800 mb-3">Additional Data</h4>
          <pre class="text-xs text-gray-900 p-3 bg-gray-100 rounded overflow-auto">{{ JSON.stringify(selectedLog.additionalData, null, 2) }}</pre>
        </div>
      </div>

      <div slot="footer" class="dialog-footer">
        <el-button @click="showDetailDialog = false">Close</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'ActivityLogsHome',
  data() {
    return {
      showFilters: false,
      showDetailDialog: false,
      selectedLog: null,
      dateRange: null,
      pageSize: 25,
      filters: {
        username: '',
        action: '',
        deviceOwner: '',
        anomali: '',
        startDate: '',
        endDate: ''
      }
    }
  },
  computed: {
    activityLogs() {
      const logs = this.$store.getters['activitylog/getActivityLogs']
      return logs
    },
    currentPage() {
      return this.$store.getters['activitylog/getCurrentPage']
    },
    totalElements() {
      return this.$store.getters['activitylog/getTotalElements']
    },
    loading() {
      return this.$store.getters['activitylog/isLoading']
    },
    error() {
      return this.$store.getters['activitylog/getError']
    },
    currentUsername() {
      return this.$store.state.info?.sub || 'Unknown'
    },
    isUserAdmin() {
      // Check if user is admin based on username or roles
      if (this.currentUsername.toLowerCase().includes('admin')) {
        return true;
      }
      // Check if user has admin role in JWT token
      const roles = this.$store.state.userRoles;
      if (roles && Array.isArray(roles)) {
        return roles.some(role => 
          role.authority === 'ROLE_ADMIN' || 
          role.authority === 'ADMIN' ||
          role === 'ROLE_ADMIN' ||
          role === 'ADMIN'
        );
      }
      return false;
    },
    totalLogs() {
      return this.totalElements || (this.activityLogs ? this.activityLogs.length : 0)
    },
    todayLogs() {
      const today = moment().format('YYYY-MM-DD')
      if (!Array.isArray(this.activityLogs)) return 0
      return this.activityLogs.filter(log => 
        moment(log.timestamp).format('YYYY-MM-DD') === today
      ).length
    },
    uniqueUsers() {
      if (!Array.isArray(this.activityLogs)) return 0
      const users = new Set(this.activityLogs.map(log => log.username))
      return users.size
    },
    loginCount() {
      const today = moment().format('YYYY-MM-DD')
      if (!Array.isArray(this.activityLogs)) return 0
      return this.activityLogs.filter(log => 
        (log.action === 'LOGIN' || log.action === 'ABSEN_PAGI' || log.action === 'ABSEN_SIANG' || log.action === 'ABSEN_SORE') && 
        moment(log.timestamp).format('YYYY-MM-DD') === today
      ).length
    }
  },
  watch: {
    error(newError) {
      if (newError) {
        this.$message.error(newError)
      }
    },
    dateRange(newRange) {
      if (newRange && newRange.length === 2) {
        this.filters.startDate = newRange[0]
        this.filters.endDate = newRange[1]
      } else {
        this.filters.startDate = ''
        this.filters.endDate = ''
      }
    }
  },
  mounted() {
    if (this.isUserAdmin) {
      this.loadActivityLogs()
    }
  },
  methods: {
    async loadActivityLogs(page = 0) {
      try {
        const params = {
          page,
          size: this.pageSize,
          filters: this.getActiveFilters()
        }
        
        if (this.isUserAdmin) {
          await this.$store.dispatch('activitylog/getActivityLogs', params)
        } else {
          await this.$store.dispatch('activitylog/getUserActivityLogs', {
            username: this.currentUsername,
            ...params
          })
        }
      } catch (error) {
        console.error('Error loading activity logs:', error)
      }
    },

    async refreshLogs() {
      await this.loadActivityLogs()
    },

    async applyFilters() {
      console.log('Applying filters:', this.filters)
      await this.loadActivityLogs(0)
    },

    onDateRangeChange(value) {
      console.log('Date range changed:', value)
      if (value && value.length === 2) {
        this.filters.startDate = value[0]
        this.filters.endDate = value[1]
      } else {
        this.filters.startDate = ''
        this.filters.endDate = ''
      }
    },

    clearFilters() {
      this.filters = {
        username: '',
        action: '',
        deviceOwner: '',
        anomali: '',
        startDate: '',
        endDate: ''
      }
      this.dateRange = null
      this.$store.dispatch('activitylog/clearFilters')
      this.loadActivityLogs(0)
    },

    async exportLogs(format = 'excel') {
      try {
        await this.$store.dispatch('activitylog/exportActivityLogs', {
          format,
          filters: this.getActiveFilters()
        })
        this.$message.success('Export completed successfully')
      } catch (error) {
        console.error('Error exporting logs:', error)
      }
    },

    handlePageChange(page) {
      this.loadActivityLogs(page - 1)
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.loadActivityLogs(0)
    },

    showLogDetail(log) {
      this.selectedLog = log
      this.showDetailDialog = true
    },

    getActiveFilters() {
      const activeFilters = {}
      
      // Add text-based filters
      if (this.filters.username && this.filters.username.trim()) {
        activeFilters.username = this.filters.username.trim()
      }
      
      if (this.filters.action && this.filters.action.trim()) {
        activeFilters.action = this.filters.action.trim()
      }
      
      if (this.filters.deviceOwner && this.filters.deviceOwner.trim()) {
        activeFilters.deviceOwner = this.filters.deviceOwner.trim()
      }
      
      if (this.filters.anomali && this.filters.anomali.trim()) {
        activeFilters.anomali = this.filters.anomali.trim()
      }
      
      // Add date filters
      if (this.filters.startDate) {
        activeFilters.startDate = this.filters.startDate
      }
      
      if (this.filters.endDate) {
        activeFilters.endDate = this.filters.endDate
      }
      
      console.log('Active filters:', activeFilters)
      return activeFilters
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      // Add 7 hours to the date
      return moment(dateString).format('DD/MM/YYYY HH:mm:ss')
    },

    formatAction(action) {
      if (!action) return 'Unknown'
      return action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())
    },

    getActionTagType(action) {
      const types = {
        'ABSEN_PAGI': 'success',
        'ABSEN_SIANG': 'warning',
        'ABSEN_SORE': 'info',
        'LOGIN': 'success',
        'LOGOUT': 'info',
        'DEVICE_REGISTER': 'primary',
        'DEVICE_REMOVE': 'warning',
        'PASSWORD_CHANGE': 'warning',
        'PROFILE_UPDATE': 'info',
        'FILE_UPLOAD': 'success',
        'FILE_DOWNLOAD': 'info',
        'API_ACCESS': 'primary'
      }
      return types[action] || 'info'
    },

    getDeviceInfo(userAgent) {
      if (!userAgent) return 'Unknown'
      
      // Simple device detection
      if (userAgent.includes('Mobile')) return 'Mobile Device'
      if (userAgent.includes('Android')) return 'Android Device'
      if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'iOS Device'
      if (userAgent.includes('Windows')) return 'Windows'
      if (userAgent.includes('Mac')) return 'macOS'
      if (userAgent.includes('Linux')) return 'Linux'
      
      return 'Unknown Device'
    },

    isAnomalous(log) {
      // Check if username and device owner are different
      if (log.deviceOwnerName && log.username) {
        return log.username.toLowerCase() !== log.deviceOwnerName.toLowerCase()
      }
      // If no device owner info, consider it anomalous
      return !log.deviceOwnerName
    }
  }
}
</script>

<style scoped>
.activity-logs {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

.filter-form .el-form-item {
  margin-bottom: 10px;
}

.el-card {
  margin-bottom: 10px;
}

.dialog-footer {
  text-align: right;
}

.space-y-4 > * + * {
  margin-top: 1rem;
}
</style>
